/**
 * DocPilot AI — Frontend Service (v3)
 *
 * What's new vs v2:
 * - deepAnalyzeDocument(): runs /api/analyze + /api/analyze/structure +
 *   /api/analyze/terminology + /api/analyze/crossref in parallel, merges
 *   results into a single AIDeepAnalysisResult.
 * - Per-endpoint helpers (analyzeStructure, analyzeTerminology, analyzeCrossref)
 *   exposed for individual calls if needed.
 * - Progress callback so the UI can show per-stage progress.
 * - Each endpoint failure is isolated: if one fails, the others still return
 *   their results.
 */

import { DocumentStructure } from "../runs/docExtractor";

// ── Configuration ──
const API_BASE_URL = "http://localhost:3001";

// ═══════════════════════════════════════════════════════════════
// Response Types — same shape as v2, plus new types for new endpoints
// ═══════════════════════════════════════════════════════════════

export interface AIIssue {
  id: string;
  paragraphIndex: number;
  paragraphText: string;
  issueType: string;
  severity: "critical" | "warning" | "info";
  confidence: number;
  currentStyle: string;
  expectedStyle: string;
  reasoning: string;
  suggestedAction: string;
  autoFixable: boolean;
  relatedRun: number;
  // Optional fields from new endpoints
  source?: "analyze" | "structure" | "terminology" | "crossref";
  affectedItems?: string[];
  term?: string;
  occurrences?: number[];
  definitionParagraph?: number | null;
  referenceText?: string;
  targetType?: string;
  targetExists?: boolean;
  nearestExisting?: string[];
}

export interface AISuggestion {
  id: string;
  paragraphIndex: number;
  paragraphText: string;
  category: string;
  message: string;
  action: string;
  confidence: number;
}

export interface AIConsistencyWarning {
  id: string;
  warningType: string;
  message: string;
  locations: number[];
  severity: string;
}

export interface AISummary {
  overview: string;
  structureValid: boolean;
  estimatedIssueCount: number;
  riskAreas: string[];
  recommendedRunOrder: number[];
}

export interface AIAnalysisResult {
  success: boolean;
  analysis: {
    summary: AISummary;
    issues: AIIssue[];
    suggestions: AISuggestion[];
    consistencyWarnings: AIConsistencyWarning[];
  };
  meta: {
    paragraphCount: number;
    elapsedSeconds: number;
    chunks?: number;
    rawIssueCount?: number;
    dedupedIssueCount?: number;
  };
}

// ── New per-endpoint result types ──

export interface AIStructureResult {
  success: boolean;
  analysis: {
    summary: {
      structuralHealth: "good" | "minor_issues" | "needs_review" | "major_issues";
      totalHeadings: number;
      maxDepth: number;
      issueCount: number;
    };
    issues: AIIssue[];
  };
  meta: {
    elapsedSeconds: number;
    headingCount: number;
    captionCount: number;
  };
}

export interface AITerminologyResult {
  success: boolean;
  analysis: {
    summary: {
      acronymCount: number;
      issueCount: number;
      termsAnalyzed: number;
    };
    issues: AIIssue[];
  };
  meta: {
    elapsedSeconds: number;
    acronymCount: number;
    phraseCount: number;
    skippedReason?: string;
  };
}

export interface AICrossrefResult {
  success: boolean;
  analysis: {
    summary: {
      totalReferences: number;
      totalTargets: number;
      brokenCount: number;
      ambiguousCount: number;
    };
    issues: AIIssue[];
  };
  meta: {
    elapsedSeconds: number;
    referenceCount: number;
    targetCount: number;
    skippedReason?: string;
  };
}

// ── Combined deep-analysis result ──

export interface AIDeepAnalysisResult {
  success: boolean;
  // Per-endpoint outcomes (errors stored as null, error message captured)
  analyze: AIAnalysisResult | null;
  structure: AIStructureResult | null;
  terminology: AITerminologyResult | null;
  crossref: AICrossrefResult | null;
  errors: { endpoint: string; message: string }[];
  // Merged & deduplicated view for the UI
  merged: {
    issues: AIIssue[];        // all issues, tagged with .source
    summary: {
      totalIssues: number;
      bySeverity: { critical: number; warning: number; info: number };
      bySource: { analyze: number; structure: number; terminology: number; crossref: number };
      autoFixableCount: number;
    };
  };
  meta: {
    totalElapsedSeconds: number;
    paragraphCount: number;
  };
}

export interface AIClassifyResult {
  success: boolean;
  result: {
    classification: string;
    expectedStyle: string;
    confidence: number;
    reasoning: string;
    alternativeClassification?: string;
    alternativeConfidence?: number;
    suggestedRun: number;
    flags: string[];
  };
}

export interface AIReportResult {
  success: boolean;
  report: {
    reportTitle: string;
    generatedAt: string;
    executiveSummary: string;
    overallStatus: string;
    statsBar: {
      totalChecks: number;
      completed: number;
      passed: number;
      warnings: number;
      errors: number;
      skipped: number;
    };
    sections: {
      title: string;
      checks: { run: number; name: string; status: string; summary: string; impact: string }[];
    }[];
    recommendations: string[];
    regulatoryNotes: string[];
  };
}

// Progress callback signature for the UI
export type ProgressCallback = (
  stage: "extract" | "analyze" | "structure" | "terminology" | "crossref" | "merge",
  pct: number,
  message: string
) => void;

// ═══════════════════════════════════════════════════════════════
// Health check
// ═══════════════════════════════════════════════════════════════

export async function checkBackendHealth(): Promise<{
  ok: boolean;
  agent?: string;
  model?: string;
  version?: string;
}> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/health`, {
      method: "GET",
      headers: { "Content-Type": "application/json" }
    });
    if (!response.ok) return { ok: false };
    const data = await response.json();
    return {
      ok: data.status === "ok",
      agent: data.agent,
      model: data.model,
      version: data.version
    };
  } catch {
    return { ok: false };
  }
}

// ═══════════════════════════════════════════════════════════════
// Generic POST helper with timeout
// ═══════════════════════════════════════════════════════════════

async function postJSON<T>(path: string, body: any, timeoutMs = 120000): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal: controller.signal
    });
    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData.error || `Server error: ${response.status}`);
    }
    return (await response.json()) as T;
  } finally {
    clearTimeout(timer);
  }
}

// ═══════════════════════════════════════════════════════════════
// Per-endpoint calls
// ═══════════════════════════════════════════════════════════════

export async function analyzeDocument(
  docStructure: DocumentStructure
): Promise<AIAnalysisResult> {
  console.log("[DocPilot] analyzeDocument()");
  return postJSON<AIAnalysisResult>("/api/analyze", { document: docStructure });
}

export async function analyzeStructure(
  docStructure: DocumentStructure
): Promise<AIStructureResult> {
  console.log("[DocPilot] analyzeStructure()");
  return postJSON<AIStructureResult>("/api/analyze/structure", { document: docStructure });
}

export async function analyzeTerminology(
  docStructure: DocumentStructure
): Promise<AITerminologyResult> {
  console.log("[DocPilot] analyzeTerminology()");
  return postJSON<AITerminologyResult>("/api/analyze/terminology", { document: docStructure });
}

export async function analyzeCrossref(
  docStructure: DocumentStructure
): Promise<AICrossrefResult> {
  console.log("[DocPilot] analyzeCrossref()");
  return postJSON<AICrossrefResult>("/api/analyze/crossref", { document: docStructure });
}

// ═══════════════════════════════════════════════════════════════
// Single-paragraph classification (unchanged)
// ═══════════════════════════════════════════════════════════════

export async function classifyParagraph(
  paragraph: { text: string; currentStyle: string; font: any; isInTable: boolean },
  context: {
    before: any[];
    after: any[];
    nearestTableDistance: number;
    nearestFigureDistance: number;
    sectionNumber: string;
  }
): Promise<AIClassifyResult> {
  return postJSON<AIClassifyResult>("/api/classify", { paragraph, context });
}

// ═══════════════════════════════════════════════════════════════
// Post-QC report
// ═══════════════════════════════════════════════════════════════

export async function generateReport(
  qcResults: { name: string; status: string; message: string; timestamp: string | null }[],
  documentInfo?: { title?: string; sectionCount?: number; pageCount?: number }
): Promise<AIReportResult> {
  console.log("[DocPilot] generateReport()");
  return postJSON<AIReportResult>("/api/report", { qcResults, documentInfo });
}

// ═══════════════════════════════════════════════════════════════
// DEEP ANALYSIS — runs all 4 endpoints in parallel and merges
// ═══════════════════════════════════════════════════════════════

export async function deepAnalyzeDocument(
  docStructure: DocumentStructure,
  onProgress?: ProgressCallback
): Promise<AIDeepAnalysisResult> {
  const startTime = Date.now();
  console.log("[DocPilot] deepAnalyzeDocument() — running all 4 endpoints in parallel");

  if (onProgress) onProgress("analyze", 10, "Sending document to AI...");

  // Fire all 4 endpoints in parallel using Promise.allSettled so one failure
  // does not abort the others. Each endpoint call wraps a stage progress update.
  const calls = [
    {
      name: "analyze",
      stage: "analyze" as const,
      promise: (async () => {
        const r = await analyzeDocument(docStructure);
        if (onProgress) onProgress("analyze", 100, `Style analysis done (${r.analysis?.issues?.length ?? 0} issues)`);
        return r;
      })()
    },
    {
      name: "structure",
      stage: "structure" as const,
      promise: (async () => {
        const r = await analyzeStructure(docStructure);
        if (onProgress) onProgress("structure", 100, `Structure analysis done (${r.analysis?.issues?.length ?? 0} issues)`);
        return r;
      })()
    },
    {
      name: "terminology",
      stage: "terminology" as const,
      promise: (async () => {
        const r = await analyzeTerminology(docStructure);
        if (onProgress) onProgress("terminology", 100, `Terminology analysis done (${r.analysis?.issues?.length ?? 0} issues)`);
        return r;
      })()
    },
    {
      name: "crossref",
      stage: "crossref" as const,
      promise: (async () => {
        const r = await analyzeCrossref(docStructure);
        if (onProgress) onProgress("crossref", 100, `Cross-reference check done (${r.analysis?.issues?.length ?? 0} issues)`);
        return r;
      })()
    }
  ];

  const results = await Promise.allSettled(calls.map(c => c.promise));

  const out: AIDeepAnalysisResult = {
    success: false,
    analyze: null,
    structure: null,
    terminology: null,
    crossref: null,
    errors: [],
    merged: {
      issues: [],
      summary: {
        totalIssues: 0,
        bySeverity: { critical: 0, warning: 0, info: 0 },
        bySource: { analyze: 0, structure: 0, terminology: 0, crossref: 0 },
        autoFixableCount: 0
      }
    },
    meta: {
      totalElapsedSeconds: 0,
      paragraphCount: docStructure.paragraphs?.length ?? 0
    }
  };

  results.forEach((res, i) => {
    const name = calls[i].name as "analyze" | "structure" | "terminology" | "crossref";
    if (res.status === "fulfilled") {
      (out as any)[name] = res.value;
    } else {
      const msg = res.reason?.message || String(res.reason);
      console.error(`[DocPilot] ${name} failed:`, msg);
      out.errors.push({ endpoint: name, message: msg });
      if (onProgress) onProgress(name as any, 100, `${name} failed: ${msg}`);
    }
  });

  // Did at least one succeed?
  out.success =
    !!(out.analyze || out.structure || out.terminology || out.crossref);

  // ── Merge issues from all four sources, tag with .source ──
  if (onProgress) onProgress("merge", 95, "Merging results...");

  const merged: AIIssue[] = [];

  if (out.analyze?.analysis?.issues) {
    for (const it of out.analyze.analysis.issues) {
      merged.push({ ...it, source: "analyze" });
    }
  }
  if (out.structure?.analysis?.issues) {
    for (const it of out.structure.analysis.issues) {
      // Structure issues may not have currentStyle/expectedStyle/paragraphText
      // — synthesize minimal fields so the UI renders consistently.
      merged.push({
        id: it.id || `struct-${merged.length}`,
        paragraphIndex: it.paragraphIndex,
        paragraphText: (it.affectedItems || []).join(" • ") || it.reasoning?.slice(0, 80) || "",
        issueType: it.issueType,
        severity: it.severity,
        confidence: it.confidence,
        currentStyle: "—",
        expectedStyle: "—",
        reasoning: it.reasoning,
        suggestedAction: it.suggestedAction,
        autoFixable: false,                            // structure issues need human review
        relatedRun: it.relatedRun ?? 10,
        source: "structure",
        affectedItems: it.affectedItems
      });
    }
  }
  if (out.terminology?.analysis?.issues) {
    for (const it of out.terminology.analysis.issues) {
      merged.push({
        id: it.id || `term-${merged.length}`,
        paragraphIndex: it.paragraphIndex,
        paragraphText: it.term || it.reasoning?.slice(0, 80) || "",
        issueType: it.issueType,
        severity: it.severity,
        confidence: it.confidence,
        currentStyle: "—",
        expectedStyle: "—",
        reasoning: it.reasoning,
        suggestedAction: it.suggestedAction,
        autoFixable: false,                            // terminology = manual edit
        relatedRun: it.relatedRun ?? 0,
        source: "terminology",
        term: it.term,
        occurrences: it.occurrences,
        definitionParagraph: it.definitionParagraph
      });
    }
  }
  if (out.crossref?.analysis?.issues) {
    for (const it of out.crossref.analysis.issues) {
      merged.push({
        id: it.id || `xref-${merged.length}`,
        paragraphIndex: it.paragraphIndex,
        paragraphText: it.referenceText || it.reasoning?.slice(0, 80) || "",
        issueType: it.issueType,
        severity: it.severity,
        confidence: it.confidence,
        currentStyle: "—",
        expectedStyle: "—",
        reasoning: it.reasoning,
        suggestedAction: it.suggestedAction,
        autoFixable: false,                            // crossref = manual fix
        relatedRun: it.relatedRun ?? 24,
        source: "crossref",
        referenceText: it.referenceText,
        targetType: it.targetType,
        targetExists: it.targetExists,
        nearestExisting: it.nearestExisting
      });
    }
  }

  // Sort by paragraphIndex then severity (critical first within same index)
  const severityWeight: Record<string, number> = { critical: 0, warning: 1, info: 2 };
  merged.sort((a, b) => {
    const ai = a.paragraphIndex ?? 1e9;
    const bi = b.paragraphIndex ?? 1e9;
    if (ai !== bi) return ai - bi;
    return (severityWeight[a.severity] ?? 3) - (severityWeight[b.severity] ?? 3);
  });

  // Build summary
  out.merged.issues = merged;
  out.merged.summary.totalIssues = merged.length;
  for (const it of merged) {
    if (it.severity === "critical") out.merged.summary.bySeverity.critical++;
    else if (it.severity === "warning") out.merged.summary.bySeverity.warning++;
    else if (it.severity === "info") out.merged.summary.bySeverity.info++;
    if (it.source && (out.merged.summary.bySource as any)[it.source] !== undefined) {
      (out.merged.summary.bySource as any)[it.source]++;
    }
    if (it.autoFixable) out.merged.summary.autoFixableCount++;
  }

  out.meta.totalElapsedSeconds = (Date.now() - startTime) / 1000;
  if (onProgress) onProgress("merge", 100, `Done — ${merged.length} total issues`);

  console.log(
    `[DocPilot] deepAnalyzeDocument() complete: ${merged.length} issues from ` +
    `${out.merged.summary.bySource.analyze}/${out.merged.summary.bySource.structure}/` +
    `${out.merged.summary.bySource.terminology}/${out.merged.summary.bySource.crossref} ` +
    `(analyze/structure/term/xref) in ${out.meta.totalElapsedSeconds.toFixed(1)}s`
  );

  return out;
}